import React from "react";
import {bookingFormActionTypes} from "./BookingFormActionTypes";
import {MANAGEMENT_TYPE} from "../Visitors";
import {LOADING} from "../../../Components/Table/TimelineTable";
import {globalErrorCondition} from "../../../Helpers/ErrorHelper";
import { DateTime } from "luxon";
import {DateFormat} from "../../../Helpers/DateTimeHelper";
import { sortObjectByKey } from "../../../Helpers/ObjectHelper";

const SET_VISITS_ARE_LOADING = 'locale/visitors/visits/set-loading';
const SET_INITIIAL_DATA_LOADED = 'locale/visitors/visits/set-initial-data-loaded';
const CLEAR_VISIT_DATA = 'locale/visitors/visits/CLEAR_VISIT_DATA';
const POPULATE_VISIT_DATA = 'locale/visitors/visits/POPULATE_VISIT_DATA';
const POPULATE_UPCOMING_VISITOR_COUNTS = 'locale/visitors/visits/POPULATE_UPCOMING_VISITOR_COUNTS';
const SET_PAGINATION = 'locale/visitors/visits/SET_PAGINATION';

const IS_UPDATE_CONFIRMATION_MODAL_OPEN = 'locale/visitors/visits/IS_UPDATE_CONFIRMATION_MODAL_OPEN';

const REMOVE_VISIT_ROW = 'locale/visitors/visits/REMOVE_VISIT_ROW';
const UPDATE_VISIT_ROW = 'locale/visitors/visits/UPDATE_VISIT_ROW';
const UPDATE_VISIT_EXTRA_DATA = 'locale/visitors/visits/UPDATE_VISIT_EXTRA_DATA';
const UPDATE_VISITOR = 'locale/visitors/visits/UPDATE_VISITOR';

const TRACK_EXPANDED_VISIT = 'locale/visitors/visits/TRACK_EXPANDED_VISIT';

export const openEditModal = (scope, visitId, visitRowIndex, visitDate) => ({
    type: `${scope}_${bookingFormActionTypes.OPEN_EDIT_MODAL}`,
    visitId: visitId,
    visitRowIndex: visitRowIndex ?? false,
    visitDate: visitDate ?? false,
})

export const closeEditModal = (scope) => ({
    type: `${scope}_${bookingFormActionTypes.CLOSE_EDIT_MODAL}`
})

export const setIsUpdateConfirmationModalOpen = (value) => ({
    type: IS_UPDATE_CONFIRMATION_MODAL_OPEN,
    value
})

export const setVisitsAreLoading = (scope, loadingType) => ({
    type: `${scope}_${SET_VISITS_ARE_LOADING}`,
    payload: {
        visitsAreLoading: loadingType,
    }
})

export const setPagination = (scope, page, perPage) => ({
    type: `${scope}_${SET_PAGINATION}`,
    payload: {
        page: page,
        perPage: perPage
    },
})

export const clearVisitData = (scope) => ({
    type: `${scope}_${CLEAR_VISIT_DATA}`,
})

/**
 *
 * @param scope
 * @param data
 * @param dateRangeStart
 * @param dateRangeEnd
 * @param direction
 * @param minRecordsExpected
 * @param filterSettingsChanged
 * @param updateType    [LOADING.NORMAL, LOADING.AT_START, LOADING.AT_END, LOADING.REFRESH]    Annoying hack so we know what type of update requested the data, so we can adjust accordingly
 * @returns {{payload: {filterSettingsChanged, dateRangeEnd, data, minRecordsExpected, dateRangeStart, direction, updateType}, type: string}}
 */
export const populateVisitData = (
    scope,
    data,
    dateRangeStart,
    dateRangeEnd,
    direction,
    minRecordsExpected,
    filterSettingsChanged = false,
    updateType = LOADING.AT_END
) => ({
    type: `${scope}_${POPULATE_VISIT_DATA}`,
    payload: {
        data: data,
        dateRangeStart: dateRangeStart,
        dateRangeEnd: dateRangeEnd,
        direction: direction,
        minRecordsExpected: minRecordsExpected,
        filterSettingsChanged: filterSettingsChanged,
        updateType: updateType
    }
})

export const populateUpcomingVisitorTotals = (scope, data) => ({
    type: `${scope}_${POPULATE_UPCOMING_VISITOR_COUNTS}`,
    payload: {
        data: data
    }
})

export const removeVisitRow = (scope, index) => ({
    type: `${scope}_${REMOVE_VISIT_ROW}`,
    index
})

export const updateVisitRow = (scope, visitRowIndex, visitRowData) => ({
    type: `${scope}_${UPDATE_VISIT_ROW}`,
    payload: {
        visitRowIndex: visitRowIndex,
        visitRowData: visitRowData,
    }
})

export const updateVisitExtraData = (scope, visitRowIndex, extraData) => ({
    type: `${scope}_${UPDATE_VISIT_EXTRA_DATA}`,
    payload: {
        visitRowIndex: visitRowIndex,
        extraData: extraData
    }
})

export const updateVisitor = (scope, visitRowIndex, visitorIndex, visitor) => ({
    type: `${scope}_${UPDATE_VISITOR}`,
    payload: {
        visitRowIndex: visitRowIndex,
        visitorIndex: parseInt(visitorIndex),
        visitor: visitor
    }
})

export const setInitialDataLoaded = (scope, initialDataLoaded) => ({
    type: `${scope}_${SET_INITIIAL_DATA_LOADED}`,
    initialDataLoaded
})

// This is a bit of a hack to keep track of how many times the user has clicked
// the 'view' or sign in buttons to expand rows in the table.
// Triggers an effect in the timeline marker which updates the marker position.
export const trackExpandedVisit = (scope) => ({
    type: `${scope}_${TRACK_EXPANDED_VISIT}`
})

export const initialState = {
    visits: [],

    upcomingBookingCounts: {},

    isUpdateConfirmationModalOpen: false,
    returnPassUpdateVisitor: false,

    editingVisitId: false,
    editingVisitRowIndex: false,
    editingVisitDate: false,

    visitsAreLoading: false,

    initialDataLoaded: false,
    hasMoreRecordsAtEnd: false,
    hasMoreRecordsAtStart: false,

    page: 1,
    perPage: 25,
    totalResults: 100,
    lastUpdated: false,

    scrollPosition: 0,

    numberOfExpandedVisits: 0,
};

export const visitsReducerFor = (scope) => {
    return (state = initialState, action) => {
        //console.log(`${scope}_${action.type}`, action.payload);

        switch (action.type) {
            case `${scope}_${SET_VISITS_ARE_LOADING}`: {
                return {
                    ...state,
                    visitsAreLoading: action.payload.visitsAreLoading,
                }
            }

            case `${scope}_${bookingFormActionTypes.OPEN_EDIT_MODAL}`: {
                return {
                    ...state,
                    editingVisitId: action.visitId,
                    editingVisitRowIndex: action.visitRowIndex,
                    editingVisitDate: action.visitDate,
                }
            }

            case `${scope}_${bookingFormActionTypes.CLOSE_EDIT_MODAL}`: {
                return {
                    ...state,
                    editingVisitId: null,
                    editingVisitRowIndex: null,
                    editingVisitDate: null
                }
            }

            case IS_UPDATE_CONFIRMATION_MODAL_OPEN: {
                return {...state, isUpdateConfirmationModalOpen: action.value}
            }

            case `${scope}_${SET_PAGINATION}`: {
                return {
                    ...state,
                    page: action.payload.page ?? state.page,
                    perPage: action.payload.perPage ?? state.perPage
                }
            }

            case `${scope}_${REMOVE_VISIT_ROW}`: {
                let newVisits = {...state.visits};

                let removedVisitIdentifiers = Array.isArray(action.index)
                    ? action.index
                    : [action.index];

                for (let i in removedVisitIdentifiers) {
                    if (!removedVisitIdentifiers.hasOwnProperty(i)) { continue; }
                    let property = removedVisitIdentifiers[i];
                    delete newVisits[property];
                }

                return {
                    ...state,
                    visits: newVisits,
                }
            }

            case `${scope}_${UPDATE_VISIT_ROW}`: {
                let {visitRowIndex, visitRowData} = action.payload;
                return {
                    ...state,
                    visits: {
                        ...state.visits,
                        [visitRowIndex]: {
                            ...state.visits[visitRowIndex],
                            ...visitRowData
                        }
                    }
                };
            }

            case `${scope}_${UPDATE_VISIT_EXTRA_DATA}`: {
                let {visitRowIndex, extraData} = action.payload;
                return {
                    ...state,
                    visits: {
                        ...state.visits,
                        [visitRowIndex]: {
                            ...state.visits[visitRowIndex],
                            visits: {
                                [0]: {
                                    ...state.visits[visitRowIndex].visits[0],
                                    extra: extraData,
                                }
                            }
                        }
                    }
                };
            }

            case `${scope}_${UPDATE_VISITOR}`: {
                let {visitRowIndex, visitorIndex, visitor} = action.payload;

                return {
                    ...state,
                    visits: {
                        ...state.visits,
                        [visitRowIndex]: {
                            ...state.visits[visitRowIndex],
                            visitors: state.visits[visitRowIndex].visitors.map((visitorItem, stateVisitorRowIndex) => {
                                return (stateVisitorRowIndex !== visitorIndex)
                                    ? visitorItem
                                    : {...visitorItem, ...visitor}
                            })
                        }
                    }
                };
            }

            case `${scope}_${CLEAR_VISIT_DATA}`: {
                return {
                    ...state,
                    visits: {},
                    hasMoreRecordsAtEnd: false,
                    hasMoreRecordsAtStart: false,
                    totalResults: null,
                    lastUpdated: DateTime.now().toFormat(DateFormat.MYSQL),
                }
            }

            case `${scope}_${POPULATE_VISIT_DATA}`: {
                let {data, dateRangeStart, dateRangeEnd, direction, minRecordsExpected, filterSettingsChanged, updateType} = action.payload;

                let newVisits = {};

                if (filterSettingsChanged) {
                    newVisits = {...data.items};
                } else if (scope === MANAGEMENT_TYPE.UPCOMING) {
                    newVisits = {...state.visits, ...data.items};
                    newVisits = sortObjectByKey(newVisits, false);

                } else if (scope === MANAGEMENT_TYPE.PAST) {
                    newVisits = {...state.visits, ...data.items};
                    newVisits = sortObjectByKey(newVisits, true);

                } else {
                    globalErrorCondition('Unknown scope when loading visits data', scope);
                }

                // Check if we have any more records for this query type
                let hasMoreRecordsAtEnd = state.hasMoreRecordsAtEnd;
                let hasMoreRecordsAtStart = state.hasMoreRecordsAtStart;

                if (updateType === LOADING.AT_START) {
                    hasMoreRecordsAtStart = !(Object.keys(data.items).length < minRecordsExpected);
                } else {
                    hasMoreRecordsAtEnd = !(Object.keys(data.items).length < minRecordsExpected);
                }

                return {
                    ...state,
                    visits: {...newVisits},
                    hasMoreRecordsAtEnd: hasMoreRecordsAtEnd,
                    hasMoreRecordsAtStart: hasMoreRecordsAtStart,
                    totalResults: data.totalRows ?? null,
                    lastUpdated: DateTime.now().toFormat(DateFormat.MYSQL),
                };
            }

            case `${scope}_${SET_INITIIAL_DATA_LOADED}`: {
                return {
                    ...state,
                    initialDataLoaded: action.initialDataLoaded,
                }
            }

            case `${scope}_${POPULATE_UPCOMING_VISITOR_COUNTS}`: {
                let upcomingBookingCounts = action.payload.data;
                return {
                    ...state,
                    upcomingBookingCounts: upcomingBookingCounts
                }
            }

            case `${scope}_${TRACK_EXPANDED_VISIT}`: {
                return {
                    ...state,
                    numberOfExpandedVisits: state.numberOfExpandedVisits + 1
                }
            }

            default:
                return state;
        }
    }
}
